import { ListBox, Select } from '@heroui/react';
import { useTranslation } from 'react-i18next';

export interface SelectOption<T extends string> {
  value: T;
  label: string;
  /** Optional small dot / emoji rendered before the label. */
  icon?: React.ReactNode;
  /** Optional secondary text rendered after the label in muted color. */
  meta?: string;
  /** Optional description shown under the label in the dropdown. */
  sublabel?: string;
  disabled?: boolean;
}

/**
 * Dropdown — the HeroUI v3 compound Select (React-Aria listbox, own overlay,
 * typeahead). The legacy in-house portal dropdown was dropped when the app
 * went HeroUI-only. Same value/onChange contract.
 */
export function SelectMenu<T extends string>(props: {
  value: T | null;
  options: SelectOption<T>[];
  onChange: (value: T) => void;
  placeholder?: string;
  ariaLabel?: string;
  disabled?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const placeholder = props.placeholder ?? t('common.select');
  return (
    <Select
      aria-label={props.ariaLabel ?? placeholder}
      placeholder={placeholder}
      variant="secondary"
      isDisabled={props.disabled}
      value={props.value ?? null}
      onChange={(v) => {
        if (v != null && !Array.isArray(v)) props.onChange(v as T);
      }}
      className={props.className ?? 'w-full'}
    >
      <Select.Trigger>
        <Select.Value />
        <Select.Indicator />
      </Select.Trigger>
      <Select.Popover>
        <ListBox>
          {props.options.map((option) => (
            <ListBox.Item key={option.value} id={option.value} textValue={option.label} isDisabled={option.disabled} className="min-h-11">
              {option.icon}
              <span className="min-w-0 flex-1">
                <span className="block">{option.label}</span>
                {option.sublabel && <span className="mt-0.5 block text-xs text-muted">{option.sublabel}</span>}
              </span>
              {option.meta && <span className="shrink-0 text-xs text-muted">{option.meta}</span>}
              <ListBox.ItemIndicator />
            </ListBox.Item>
          ))}
        </ListBox>
      </Select.Popover>
    </Select>
  );
}
